/**
 * bridge/share_controller.js — "share" bridge component (Stimulus + Strada).
 *
 * Capability-tier bridge component: opens a native share sheet for a URL (plus
 * optional text/title), e.g. a quote or invoice PDF link. Stimulus identifier
 * "bridge--share" (glob-derived from this subdir); Strada component name "share"
 * MUST match the iOS class var name (native/ios/Chippie/Bridge/ShareComponent.swift).
 *
 * Why it extends Strada's BridgeComponent: the iOS share sheet is reached via
 * this.send("share", ...), which only works on a connected bridge component
 * inside the iOS Hotwire Native shell. Extending BridgeComponent gives us that
 * send() transport AND the UA-gated connect() (it only connects in a native shell).
 *
 * Three-way transport: rather than each caller branching on platform, this
 * controller registers a single share() impl set onto the window.ChippieBridge
 * facade ({ ios, android, pwa }) on connect. The facade (chippie-bridge.js) owns
 * platform detection and picks the right impl per call. Callers use
 * window.ChippieBridge.share({ url, text, title }).
 *   - ios:     this.send("share", data) over Strada to ShareComponent.swift
 *   - android: window.ChippieNative.share(url, text, title) (@JavascriptInterface)
 *   - pwa:     navigator.share(...) (Web Share API), falling back to a no-op when
 *              unsupported (e.g. desktop) — clipboard copy is out of scope here.
 *
 * Template wiring (either path):
 *   <a data-controller="bridge--share"
 *      data-bridge--share-url-value="{{ pdf_url }}"
 *      data-bridge--share-title-value="Invoice INV-123"
 *      data-action="bridge--share#share">Share</a>
 * Or call window.ChippieBridge.share({ url, text, title }) directly from any JS.
 *
 * The Android + PWA impls carry no per-instance state, so they are registered at
 * module load via registerStandalone() and ChippieBridge.share() resolves
 * everywhere. Only the ios impl needs a live Strada instance for this.send, so
 * connect() overrides it while connected and disconnect() reverts to the
 * placeholder. Registering solely on connect (as this file used to) left
 * ChippieBridge.share UNDEFINED on PWA and desktop, where BridgeComponent's
 * UA-gated connect() never fires — callers got a TypeError, not a no-op.
 *
 * Web side adapted from joemasilotti/bridge-components share_controller.js.
 */

import { BridgeComponent } from "@hotwired/hotwire-native-bridge";

// Normalise to a flat { url, text, title }; default url to the current page.
// Module scope so the standalone impls and the connected controller share it.
function payload(data) {
  data = data || {};
  return {
    url: data.url || window.location.href,
    text: data.text || "",
    title: data.title || "",
  };
}

function androidShare(data) {
  const p = payload(data);
  if (!window.ChippieNative || !window.ChippieNative.share) return;
  try { window.ChippieNative.share(p.url, p.text, p.title); } catch (_e) { /* bridge gone */ }
}

function pwaShare(data) {
  const p = payload(data);
  if (!navigator.share) return;
  navigator.share({ url: p.url, text: p.text, title: p.title });
}

function registerStandalone() {
  if (!window.ChippieBridge || !window.ChippieBridge.register) return;
  window.ChippieBridge.register("share", {
    // ios is overridden by the controller's connect() with a real send; this
    // placeholder is a safe no-op if somehow selected before connect.
    ios: () => {},
    android: androidShare,
    pwa: pwaShare,
  });
}

// Facade may load after this module; register as soon as both are present.
if (window.ChippieBridge) {
  registerStandalone();
} else {
  document.addEventListener("DOMContentLoaded", registerStandalone, { once: true });
}

export default class extends BridgeComponent {
  static component = "share";

  static values = {
    url: String,
    text: String,
    title: String,
  };

  connect() {
    super.connect();
    if (!window.ChippieBridge || !window.ChippieBridge.register) return;
    window.ChippieBridge.register("share", {
      ios: (data) => this.send("share", payload(data), () => {}),
      android: androidShare,
      pwa: pwaShare,
    });
  }

  disconnect() {
    // Drop the live-send impl back to the standalone placeholder so a stale
    // this.send is never invoked. Deleting the facade method outright (the old
    // behaviour) removed the capability from Android and PWA too.
    registerStandalone();
    super.disconnect();
  }

  // data-action="bridge--share#share" trigger — shares this element's values.
  share(event) {
    if (event) event.preventDefault();
    window.ChippieBridge.share({
      url: this.urlValue,
      text: this.textValue,
      title: this.titleValue,
    });
  }
}
